import { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { extUrl } from '../../lib/url';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import {
  Shield, ShieldAlert, AlertTriangle, Ban, UserX, Clock, EyeOff, RefreshCw,
  CheckCircle2, ChevronRight, Flag, Image as ImageIcon, MessageSquare, User
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_STYLES = {
  spam: { label: 'Spam', color: 'bg-amber-100 text-amber-700' },
  harassment: { label: 'Harassment', color: 'bg-orange-100 text-orange-700' },
  sexual: { label: 'Sexual', color: 'bg-rose-100 text-rose-700' },
  hate: { label: 'Hate', color: 'bg-red-100 text-red-700' },
  violence: { label: 'Violence', color: 'bg-red-100 text-red-700' },
  self_harm: { label: 'Self-harm', color: 'bg-purple-100 text-purple-700' },
  identity_mismatch: { label: 'Identity', color: 'bg-blue-100 text-blue-700' },
  unsolicited_image: { label: 'Unsolicited image', color: 'bg-rose-100 text-rose-700' },
  report: { label: 'Reported', color: 'bg-slate-100 text-slate-700' },
};

const ACTIONS = [
  { value: 'dismiss', label: 'Dismiss', color: 'bg-slate-100 text-slate-700', icon: CheckCircle2 },
  { value: 'warn', label: 'Warn', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  { value: 'restrict_dms', label: 'Restrict DMs', color: 'bg-orange-100 text-orange-700', icon: EyeOff },
  { value: 'shadow_limit', label: 'Shadow limit', color: 'bg-purple-100 text-purple-700', icon: EyeOff },
  { value: 'require_reverify', label: 'Require re-verify', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  { value: 'suspend_7d', label: 'Suspend 7d', color: 'bg-amber-100 text-amber-700', icon: Clock },
  { value: 'suspend_30d', label: 'Suspend 30d', color: 'bg-orange-100 text-orange-700', icon: Clock },
  { value: 'ban', label: 'Ban', color: 'bg-red-100 text-red-700', icon: Ban },
];

function severityColor(sev) {
  if (sev >= 0.8) return 'bg-red-100 text-red-700';
  if (sev >= 0.6) return 'bg-orange-100 text-orange-700';
  if (sev >= 0.4) return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-700';
}

export default function TrustSafetyTab() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionReason, setActionReason] = useState('');

  const load = async (status = statusFilter) => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        adminAPI.trustSafetyStats(),
        adminAPI.trustSafetyQueue({ status, limit: 50 }),
      ]);
      setStats(s.data);
      setQueue(q.data.escalations || []);
    } catch (e) {
      toast.error('Failed to load Trust & Safety data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(statusFilter); /* eslint-disable-next-line */ }, [statusFilter]);

  const openDetail = async (esc) => {
    setSelected(esc);
    setDetailLoading(true);
    try {
      const res = await adminAPI.trustSafetyEscalation(esc.escalation_id);
      setDetail(res.data);
    } catch (e) {
      toast.error('Could not load escalation details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setSelected(null); setDetail(null); setActionReason(''); };

  const takeAction = async (action) => {
    if (!selected) return;
    try {
      await adminAPI.trustSafetyAction({
        escalation_id: selected.escalation_id,
        user_id: selected.user_id,
        action,
        reason: actionReason || `Admin action: ${action}`,
      });
      toast.success(`Action applied: ${action}`);
      closeDetail();
      load(statusFilter);
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const recompute = async () => {
    try {
      const r = await adminAPI.trustSafetyRecompute();
      toast.success(`Aggregator touched ${r.data.escalations_touched || 0} escalations`);
      load(statusFilter);
    } catch (e) {
      toast.error('Recompute failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header + stats */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading font-bold text-lg text-dark-slate flex items-center gap-2">
            <Shield className="w-5 h-5 text-mint-fresh" /> Trust &amp; Safety
          </h3>
          <p className="text-sm text-slate-gray">Claude-triaged escalations, user reports, and moderation actions.</p>
        </div>
        <Button onClick={recompute} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Recompute
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <Stat label="Open" value={stats.open_escalations} color="red" />
          <Stat label="Reviewing" value={stats.reviewing} color="amber" />
          <Stat label="Actioned 7d" value={stats.actioned_7d} color="emerald" />
          <Stat label="Signals 24h" value={stats.signals_24h} color="blue" />
          <Stat label="Auto-actions 7d" value={stats.auto_actions_7d} color="purple" />
          <Stat label="DM restricted" value={stats.restricted_users} color="orange" />
          <Stat label="Suspended" value={stats.suspended_users} color="amber" />
          <Stat label="Banned" value={stats.banned_users} color="red" />
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1 w-fit">
        {['open', 'reviewing', 'actioned', 'dismissed', 'all'].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${statusFilter === s ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'}`}>
            {s}
          </button>
        ))}
      </div>

      {/* Queue */}
      <div className="bg-white rounded-2xl border border-slate-100">
        {loading ? (
          <div className="p-8 text-center text-slate-gray">Loading escalations…</div>
        ) : queue.length === 0 ? (
          <div className="p-8 text-center">
            <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-dark-slate">Nothing to review</p>
            <p className="text-sm text-slate-gray mt-1">No {statusFilter} escalations right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {queue.map((esc) => {
              const user = esc.user || {};
              const cat = CATEGORY_STYLES[esc.category] || CATEGORY_STYLES.spam;
              return (
                <button key={esc.escalation_id} onClick={() => openDetail(esc)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left">
                  {user.profile_photo ? (
                    <img src={user.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-dark-slate truncate">{user.name || esc.user_id}</p>
                      <Badge className={cat.color + ' text-[10px]'}>{cat.label}</Badge>
                      <Badge className={severityColor(esc.severity) + ' text-[10px]'}>
                        sev {(esc.severity || 0).toFixed(2)}
                      </Badge>
                      {esc.signal_count > 1 && (
                        <Badge variant="outline" className="text-[10px]">{esc.signal_count} signals</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-gray truncate">
                      {esc.claude_summary || `${cat.label} signals detected.`}
                    </p>
                    {esc.claude_recommendation && (
                      <p className="text-[11px] text-mint-fresh mt-0.5">
                        Recommend: <span className="font-semibold">{esc.claude_recommendation}</span>
                        {typeof esc.claude_confidence === 'number' && ` (${Math.round(esc.claude_confidence * 100)}%)`}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Escalation review</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 text-center text-slate-gray">Loading…</div>
          ) : detail ? (
            <div className="space-y-5">
              {/* User card */}
              <div className="flex gap-3 p-4 bg-slate-50 rounded-xl">
                {detail.user?.profile_photo ? (
                  <img src={detail.user.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center">
                    <User className="w-7 h-7 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-semibold text-dark-slate">{detail.user?.name}</p>
                  <p className="text-sm text-slate-gray">{detail.user?.email}</p>
                  <p className="text-xs text-slate-gray mt-1">
                    {detail.user?.institution} • Class of {detail.user?.graduation_year} • Joined {detail.user?.created_at?.slice(0, 10)}
                  </p>
                  {detail.user?.linkedin_url && (
                    <a href={extUrl(detail.user.linkedin_url)} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">
                      LinkedIn profile
                    </a>
                  )}
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {detail.user?.status && detail.user.status !== 'active' && (
                      <Badge className="bg-red-100 text-red-700 text-[10px] capitalize">{detail.user.status}</Badge>
                    )}
                    {detail.user?.moderation?.restrict_dms && (
                      <Badge className="bg-orange-100 text-orange-700 text-[10px]">DM restricted</Badge>
                    )}
                    {detail.user?.moderation?.shadow_limit && (
                      <Badge className="bg-purple-100 text-purple-700 text-[10px]">Shadow-limited</Badge>
                    )}
                    {detail.user?.moderation?.strike_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px]">{detail.user.moderation.strike_count} strike(s)</Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Claude summary */}
              {detail.escalation?.claude_summary && (
                <div className="p-4 bg-mint-fresh/5 border border-mint-fresh/30 rounded-xl">
                  <p className="text-xs font-semibold text-mint-fresh uppercase tracking-wide mb-1">Claude summary</p>
                  <p className="text-sm text-dark-slate">{detail.escalation.claude_summary}</p>
                  {detail.escalation.claude_recommendation && (
                    <p className="text-xs text-dark-slate mt-2">
                      Recommended: <span className="font-semibold">{detail.escalation.claude_recommendation}</span>
                      {typeof detail.escalation.claude_confidence === 'number' && ` (confidence ${Math.round(detail.escalation.claude_confidence * 100)}%)`}
                    </p>
                  )}
                </div>
              )}

              {/* Signals */}
              <div>
                <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-2">Signals ({detail.signals?.length || 0})</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(detail.signals || []).map((s) => {
                    const cat = CATEGORY_STYLES[s.category] || CATEGORY_STYLES.spam;
                    return (
                      <div key={s.signal_id} className="p-3 bg-white border border-slate-100 rounded-lg text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={cat.color + ' text-[10px]'}>{cat.label}</Badge>
                          <Badge className={severityColor(s.severity) + ' text-[10px]'}>sev {s.severity.toFixed(2)}</Badge>
                          <span className="text-[10px] text-slate-gray">{s.source}</span>
                          <span className="text-[10px] text-slate-gray ml-auto">{s.created_at?.slice(0, 16).replace('T', ' ')}</span>
                        </div>
                        {s.evidence?.text_excerpt && (
                          <p className="text-sm text-slate-gray italic">"{s.evidence.text_excerpt}"</p>
                        )}
                        {s.evidence?.reasoning && (
                          <p className="text-xs text-slate-gray mt-1">{s.evidence.reasoning}</p>
                        )}
                        {s.evidence?.image_url && (
                          <div className="mt-2">
                            <div className="relative inline-block">
                              <img src={s.evidence.image_url} alt="evidence" className="max-h-32 rounded-lg filter blur-lg hover:blur-0 transition-all cursor-pointer" />
                              <Badge className="absolute top-1 right-1 bg-black/60 text-white text-[10px]">
                                <ImageIcon className="w-3 h-3 mr-1" />Hover to reveal
                              </Badge>
                            </div>
                          </div>
                        )}
                        {s.evidence?.mismatch_signals?.length > 0 && (
                          <ul className="text-xs text-slate-gray mt-1 list-disc list-inside">
                            {s.evidence.mismatch_signals.map((m, i) => <li key={i}>{m}</li>)}
                          </ul>
                        )}
                        {s.evidence?.distinct_recipients_24h && (
                          <p className="text-xs text-slate-gray">Sent to {s.evidence.distinct_recipients_24h} recipients in 24h</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Reports */}
              {detail.reports?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-2">User reports ({detail.reports.length})</p>
                  <div className="space-y-2">
                    {detail.reports.map((r) => (
                      <div key={r.report_id} className="p-2 bg-white border border-slate-100 rounded-lg text-xs">
                        <Flag className="w-3 h-3 inline mr-1 text-red-500" />
                        <span className="font-medium">{r.reason}</span>
                        {r.details && <span className="text-slate-gray"> — {r.details}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Past actions */}
              {detail.past_actions?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-2">Past actions</p>
                  <div className="space-y-1">
                    {detail.past_actions.map((a) => (
                      <p key={a.action_id} className="text-xs text-slate-gray">
                        {a.created_at?.slice(0, 10)} • <span className="font-semibold text-dark-slate">{a.action}</span>
                        {a.auto && <Badge className="ml-1 bg-blue-100 text-blue-700 text-[9px]">auto</Badge>}
                        {a.reason && ` — ${a.reason}`}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Action bar */}
              <div>
                <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-2">Take action</p>
                <Textarea placeholder="Reason (will be shown to the user if applicable)" value={actionReason} onChange={(e) => setActionReason(e.target.value)} className="mb-2 min-h-[60px]" />
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((a) => {
                    const Icon = a.icon;
                    return (
                      <button key={a.value} onClick={() => takeAction(a.value)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:opacity-80 ${a.color}`}>
                        <Icon className="w-4 h-4" />{a.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-slate-100 text-center">
      <p className={`text-xl font-heading font-bold text-${color}-600`}>{value ?? 0}</p>
      <p className="text-[11px] text-slate-gray mt-0.5">{label}</p>
    </div>
  );
}
